import { H5 } from '@wheely/ui-kit';
import cn from 'classnames';
import React, { useCallback } from 'react';

import { ICard, ICardItem } from '@/pages/Todo/types';
import { Icon } from '@src-common/view/components/Icon';

import s from './styles.module.css';

const Item = ({ title, internalLink, action, status, tipIntent = 'info', tipText }: ICardItem) => (
  <button
    key={title}
    data-url={internalLink}
    onClick={action}
    className={s.item}
    data-test-id="todo-card-item"
  >
    <div className={s.itemLeft}>
      <div className={s.itemTitle}>{title}</div>
      {status && (
        <div
          data-test-id="todo-card-item__status"
          className={cn({
            [s.itemDanger]: tipIntent === 'danger',
            [s.itemPending]: tipIntent === 'info',
            [s.itemActive]: tipIntent === 'success',
          })}
        >
          {tipText}
        </div>
      )}
    </div>
    <Icon icon="chevron-to-end" className={s.itemIcon} />
  </button>
);

const CardView: React.FC<ICard> = React.memo(
  ({
    actionTracker,
    action,
    cardStatus,
    isBlocked,
    title,
    desc,
    items,
    actionTitle,
    actionExternalLink,
  }) => {
    const isDone = cardStatus === 'done';

    const handleClickAction = useCallback(() => {
      actionTracker?.();
      action?.();
    }, [action, actionTracker]);

    return (
      <div className={cn(s.card, { [s.cardBlocked]: isBlocked })} data-test-id="todo-card">
        <H5 capitalize={true} className={s.cardTitle} data-test-id="todo-card__title">
          {title}
          {isBlocked && (
            <Icon
              icon="lock"
              className={s.iconBlock}
              size="24px"
              data-test-id="todo-card__blocked-icon"
            />
          )}
          {isDone && (
            <Icon
              icon="checkmark-in-circle"
              className={s.iconDone}
              size="24px"
              data-test-id="todo-card__done-icon"
            />
          )}
        </H5>
        {!isDone && (
          <p className={s.desc} data-test-id="todo-card__description">
            {desc}
          </p>
        )}
        {items && !isBlocked && !isDone && items.map((item, idx) => <Item key={idx} {...item} />)}
        {actionTitle &&
          !isBlocked &&
          !isDone &&
          (action ? (
            <button
              className={s.action}
              onClick={handleClickAction}
              data-test-id="todo-card__action-button"
            >
              <H5 tag="span" capitalize={true}>
                {actionTitle}
              </H5>
              <Icon icon="arrow-to-end-in-circle" className={s.actionIcon} size="24px" />
            </button>
          ) : (
            <a
              href={actionExternalLink}
              className={s.action}
              target={'_blank'}
              rel="noreferrer"
              onClick={actionTracker}
              data-test-id="todo-card__action-link"
            >
              <H5 tag="span" capitalize={true}>
                {actionTitle}
              </H5>
              <Icon icon="arrow-to-end-in-circle" className={s.actionIcon} size="24px" />
            </a>
          ))}
      </div>
    );
  },
);

CardView.displayName = 'CardView';

export { CardView };
